import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api';
import { useUserPermissions, getUserStatusMessage } from '../hooks/useUserPermissions';

interface UserAccountData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  company: string;
  vatNumber: string;
  kvkNumber: string;
  vatCheckApproved: boolean;
  billingAddress: {
    company: string;
    name: string;
    street: string;
    number: string;
    postal: string;
    city: string;
    country: string;
    countryCode?: string;
  };
  deliveryAddress: {
    company: string;
    name: string;
    street: string;
    number: string;
    postal: string;
    city: string;
    country: string;
    countryCode?: string;
  };
}

interface AvailableCountry {
  code: string;
  name: string;
  priceLanguage: string;
  vatPercentage: number;
}

// Global state for unsaved changes warning
let globalUnsavedChangesChecker: (() => boolean) | null = null;

export const setGlobalUnsavedChangesChecker = (checker: (() => boolean) | null) => {
  globalUnsavedChangesChecker = checker;
};

export const checkGlobalUnsavedChanges = (): boolean => {
  return globalUnsavedChangesChecker ? globalUnsavedChangesChecker() : true;
};

const UserAccountPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUserSettings } = useAuth();
  const permissions = useUserPermissions();
  
  // Check if billing fields should be disabled based on user status
  const shouldDisableBillingFields = user?.status !== 'new';
  
  const [formData, setFormData] = useState<UserAccountData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    company: '',
    vatNumber: '',
    kvkNumber: '',
    vatCheckApproved: false,
    billingAddress: {
      company: '',
      name: '',
      street: '',
      number: '',
      postal: '',
      city: '',
      country: 'Nederland'
    },
    deliveryAddress: {
      company: '',
      name: '',
      street: '',
      number: '',
      postal: '',
      city: '',
      country: 'Nederland'
    }
  });

  const [sameAsDelivery, setSameAsDelivery] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState<UserAccountData | null>(null);
  const [availableCountries, setAvailableCountries] = useState<AvailableCountry[]>([]);
  const [vatValidation, setVatValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    validationData: any | null;
    error: string | null;
  }>({
    isValidating: false,
    isValid: null,
    validationData: null,
    error: null
  });
  
  console.log('Current formData:', formData);

  // Load user settings from AuthContext
  useEffect(() => {
    // Load available countries first
    loadAvailableCountries();
    
    if (user?.settings) {
      console.log('Loading settings from AuthContext:', user.settings);
      const settingsWithDefaults = {
        ...user.settings,
        email: user.email,
        phoneNumber: (user.settings as any).phoneNumber || '',
        vatCheckApproved: (user.settings as any).vatCheckApproved || false
      };
      setFormData(settingsWithDefaults);
      setOriginalData(settingsWithDefaults);
      
      // Check if delivery and billing addresses are the same
      // For new users, only set checkbox if explicitly saved as same
      const { billingAddress, deliveryAddress } = user.settings;
      const hasAddressData = Boolean(billingAddress.street || deliveryAddress.street);
      const isSame = hasAddressData && JSON.stringify(deliveryAddress) === JSON.stringify(billingAddress);
      setSameAsDelivery(isSame);
      console.log('Same as delivery:', isSame);
      setIsLoading(false);
    } else if (user && !user.settings) {
      // Fallback: load settings if not available in user object
      const loadUserSettings = async () => {
        try {
          const response = await fetch(getApiUrl('/api/web/user/settings'), {
            method: 'GET',
            credentials: 'include',
          });

          if (response.ok) {
            const result = await response.json();
            if (result.meta?.result === 'success' && result.data) {
              const settingsWithDefaults = {
                ...result.data,
                email: user?.email || '',
                phoneNumber: result.data.phoneNumber || '',
                vatCheckApproved: result.data.vatCheckApproved || false
              };
              setFormData(settingsWithDefaults);
              setOriginalData(settingsWithDefaults);
              
              const { billingAddress, deliveryAddress } = result.data;
              const hasAddressData = Boolean(billingAddress.street || deliveryAddress.street);
              const isSame = hasAddressData && JSON.stringify(deliveryAddress) === JSON.stringify(billingAddress);
              setSameAsDelivery(isSame);
            }
          }
        } catch (error) {
          console.error('Error loading user settings:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadUserSettings();
    }
  }, [user]);

  // Check for unsaved changes
  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, originalData]);

  // Setup global unsaved changes checker
  useEffect(() => {
    const checker = () => {
      if (hasUnsavedChanges) {
        return window.confirm(t('account.confirmLeave'));
      }
      return true;
    };

    setGlobalUnsavedChangesChecker(checker);

    return () => {
      setGlobalUnsavedChangesChecker(null);
    };
  }, [hasUnsavedChanges, t]);

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleInputChange = (field: keyof UserAccountData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset VAT validation when VAT number changes
    if (field === 'vatNumber') {
      setVatValidation({
        isValidating: false,
        isValid: null,
        validationData: null,
        error: null
      });
      
      // Also reset vatCheckApproved
      setFormData(prev => ({
        ...prev,
        [field]: value,
        vatCheckApproved: false
      }));
      return;
    }
    
    if (value.trim()) {
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(field);
        return newErrors;
      });
    }
  };

  const handleAddressChange = (
    addressType: 'billingAddress' | 'deliveryAddress',
    field: keyof UserAccountData['billingAddress'],
    value: string
  ) => {
    setFormData(prev => {
      const updatedAddress = {
        ...prev[addressType],
        [field]: value
      };

      // If "same as billing" is checked and we're updating billing address,
      // also update delivery address
      if (sameAsDelivery && addressType === 'billingAddress') {
        return {
          ...prev,
          billingAddress: updatedAddress,
          deliveryAddress: updatedAddress
        };
      } else {
        return {
          ...prev,
          [addressType]: updatedAddress
        };
      }
    });

    if (value.trim()) {
      const errorKey = `${addressType}_${field}`;
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(errorKey);
        return newErrors;
      });
    }
  };

  const handleSameAsBillingToggle = (checked: boolean) => {
    setSameAsDelivery(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        deliveryAddress: { ...prev.billingAddress }
      }));
    }
  };

  const handleCountryChange = (
    addressType: 'billingAddress' | 'deliveryAddress',
    countryCode: string
  ) => {
    const selectedCountry = availableCountries.find(c => c.code === countryCode);
    if (selectedCountry) {
      setFormData(prev => ({
        ...prev,
        [addressType]: {
          ...prev[addressType],
          country: selectedCountry.name,
          countryCode: selectedCountry.code
        }
      }));

      // Clear validation error
      const errorKey = `${addressType}_country`;
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(errorKey);
        return newErrors;
      });
    }
  };

  const loadAvailableCountries = async () => {
    try {
      const response = await fetch(getApiUrl('/api/web/available-countries'), {
        method: 'GET',
        credentials: 'include',
      });

      const result = await response.json();
      
      if (response.ok && result.meta?.result === 'success') {
        setAvailableCountries(result.data.countries || []);
      } else {
        console.error('Failed to load available countries:', result);
        // Fallback to default countries if API fails
        setAvailableCountries([
          { code: 'NL', name: 'Nederland', priceLanguage: 'nl', vatPercentage: 21 }
        ]);
      }
    } catch (error) {
      console.error('Error loading available countries:', error);
      // Fallback to default countries if API fails
      setAvailableCountries([
        { code: 'NL', name: 'Nederland', priceLanguage: 'nl', vatPercentage: 21 }
      ]);
    }
  };

  const validateVatNumber = async () => {
    if (!formData.vatNumber.trim()) {
      return;
    }

    setVatValidation(prev => ({
      ...prev,
      isValidating: true,
      error: null
    }));

    try {
      const response = await fetch(
        getApiUrl(`/api/web/validate-vat?vat_number=${encodeURIComponent(formData.vatNumber)}`),
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      const result = await response.json();
      
      if (response.ok && result.meta?.result === 'success') {
        const isValid = result.data.valid || false;
        
        setVatValidation({
          isValidating: false,
          isValid,
          validationData: result.data,
          error: null
        });

        // Update vatCheckApproved based on validation result
        setFormData(prev => ({
          ...prev,
          vatCheckApproved: isValid
        }));

        // Check if company name should be updated
        if (isValid && result.data.company_name && 
            result.data.company_name !== formData.company &&
            permissions.canEditProfile) {
          
          const shouldUpdate = window.confirm(
            `De gevalideerde bedrijfsnaam is "${result.data.company_name}". ` +
            `Wilt u de huidige bedrijfsnaam "${formData.company}" hiermee vervangen?`
          );
          
          if (shouldUpdate) {
            setFormData(prev => ({
              ...prev,
              company: result.data.company_name
            }));
          }
        }
      } else {
        throw new Error(result.meta?.message || 'VAT validation failed');
      }
    } catch (error) {
      console.error('VAT validation error:', error);
      setVatValidation({
        isValidating: false,
        isValid: false,
        validationData: null,
        error: error instanceof Error ? error.message : 'VAT validation failed'
      });
      
      // Set vatCheckApproved to false on validation error
      setFormData(prev => ({
        ...prev,
        vatCheckApproved: false
      }));
    }
  };

  const validateForm = () => {
    const requiredFields = [
      { value: formData.firstName, key: 'firstName' },
      { value: formData.lastName, key: 'lastName' },
      { value: formData.company, key: 'company' },
      { value: formData.vatNumber, key: 'vatNumber' },
      { value: formData.kvkNumber, key: 'kvkNumber' },
      { value: formData.deliveryAddress.company, key: 'deliveryAddress_company' },
      { value: formData.deliveryAddress.name, key: 'deliveryAddress_name' },
      { value: formData.deliveryAddress.street, key: 'deliveryAddress_street' },
      { value: formData.deliveryAddress.number, key: 'deliveryAddress_number' },
      { value: formData.deliveryAddress.postal, key: 'deliveryAddress_postal' },
      { value: formData.deliveryAddress.city, key: 'deliveryAddress_city' },
      { value: formData.billingAddress.company, key: 'billingAddress_company' },
      { value: formData.billingAddress.name, key: 'billingAddress_name' },
      { value: formData.billingAddress.street, key: 'billingAddress_street' },
      { value: formData.billingAddress.number, key: 'billingAddress_number' },
      { value: formData.billingAddress.postal, key: 'billingAddress_postal' },
      { value: formData.billingAddress.city, key: 'billingAddress_city' }
    ];

    const emptyFieldKeys = requiredFields
      .filter(field => !field.value?.trim())
      .map(field => field.key);
    
    setValidationErrors(new Set(emptyFieldKeys));
    
    return emptyFieldKeys.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    
    try {
      const response = await fetch(getApiUrl('/api/web/user/settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      
      if (response.ok && result.meta?.result === 'success') {
        setOriginalData(formData);
        setHasUnsavedChanges(false);
        // Update AuthContext with new settings (add current language)
        updateUserSettings({
          ...formData,
          language: i18n.language
        });
        alert(t('account.saveSuccess'));
      } else {
        throw new Error(result.meta?.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save user account data:', error);
      alert(t('account.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestApproval = async () => {
    if (!validateForm()) {
      return;
    }

    // Clear the global unsaved changes checker before starting
    // to prevent confirmation dialogs during the save & approval process
    setGlobalUnsavedChangesChecker(null);
    setHasUnsavedChanges(false);

    setIsSaving(true);
    
    try {
      // First save the user settings
      const saveResponse = await fetch(getApiUrl('/api/web/user/settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save settings');
      }

      // Then request approval by updating user status
      const approvalResponse = await fetch(getApiUrl('/api/web/user/request-approval'), {
        method: 'POST',
        credentials: 'include'
      });

      const result = await approvalResponse.json();
      
      if (approvalResponse.ok && result.meta?.result === 'success') {
        // Update original data and clear unsaved changes flag
        setOriginalData(formData);
        setHasUnsavedChanges(false);
        updateUserSettings({
          ...formData,
          language: i18n.language
        });
        
        // Clear the global unsaved changes checker before reload
        setGlobalUnsavedChangesChecker(null);
        
        // Refresh the page to show the updated status
        window.location.reload();
      } else {
        throw new Error(result.meta?.message || 'Failed to request approval');
      }
    } catch (error) {
      console.error('Failed to request approval:', error);
      alert(t('account.approvalError'));
    } finally {
      setIsSaving(false);
    }
  };

  const renderAddressForm = (
    title: string,
    address: UserAccountData['billingAddress'],
    onChange: (field: keyof UserAccountData['billingAddress'], value: string) => void,
    disabled: boolean = false,
    addressType: 'billingAddress' | 'deliveryAddress',
    onCountryChange?: (countryCode: string) => void
  ) => (
    <div className="mb-3">
      <h6 className="mb-2">{title}</h6>
      <Row className="g-2">
        <Col sm={6}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-company`}
              type="text"
              placeholder="Bedrijf"
              size="sm"
              value={address.company}
              onChange={(e) => onChange('company', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_company`)}
            />
            <Form.Label htmlFor={`${addressType}-company`}>
              {t('account.address.company')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={6}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-name`}
              type="text"
              placeholder="Contactpersoon"
              size="sm"
              value={address.name}
              onChange={(e) => onChange('name', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_name`)}
            />
            <Form.Label htmlFor={`${addressType}-name`}>
              {t('account.address.name')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
      </Row>
      <Row className="g-2">
        <Col sm={8}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-street`}
              type="text"
              placeholder="Straat"
              size="sm"
              value={address.street}
              onChange={(e) => onChange('street', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_street`)}
            />
            <Form.Label htmlFor={`${addressType}-street`}>
              {t('account.address.street')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-number`}
              type="text"
              placeholder="Nummer"
              size="sm"
              value={address.number}
              onChange={(e) => onChange('number', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_number`)}
            />
            <Form.Label htmlFor={`${addressType}-number`}>
              {t('account.address.number')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
      </Row>
      <Row className="g-2">
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-postal`}
              type="text"
              placeholder="Postcode"
              size="sm"
              value={address.postal}
              onChange={(e) => onChange('postal', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_postal`)}
            />
            <Form.Label htmlFor={`${addressType}-postal`}>
              {t('account.address.postal')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-city`}
              type="text"
              placeholder="Plaats"
              size="sm"
              value={address.city}
              onChange={(e) => onChange('city', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_city`)}
            />
            <Form.Label htmlFor={`${addressType}-city`}>
              {t('account.address.city')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Select
              id={`${addressType}-country`}
              size="sm"
              value={address.countryCode || ''}
              onChange={(e) => {
                if (typeof onCountryChange === 'function') {
                  onCountryChange(e.target.value);
                } else {
                  onChange('country', e.target.value);
                }
              }}
              disabled={disabled}
            >
              <option value="">{t('account.address.selectCountry')}</option>
              {availableCountries.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
            <Form.Label htmlFor={`${addressType}-country`}>{t('account.address.country')}</Form.Label>
          </Form.Floating>
        </Col>
      </Row>
    </div>
  );

  if (isLoading) {
    return (
      <Container className="py-4">
        <Row>
          <Col lg={8} className="mx-auto">
            <Card>
              <Card.Body className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 mb-0">{t('common.loading')}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row>
        <Col lg={8} className="mx-auto">
          <Card>
            <Card.Header>
              <h4 className="mb-0">
                <i className="fas fa-user me-2"></i>
                {permissions.showApprovalRequest ? t('account.requestTitle') : t('account.title')}
              </h4>
            </Card.Header>
            <Card.Body>
              {/* Status Message */}
              {user?.status && user.status !== 'approved' && (
                <Alert variant={user.status === 'rejected' ? 'danger' : 'info'} className="mb-4">
                  <i className={`fas ${user.status === 'rejected' ? 'fa-exclamation-circle' : 'fa-info-circle'} me-2`}></i>
                  {getUserStatusMessage(user.status as any, t)}
                </Alert>
              )}

              {/* Personal Information */}
              <div className="mb-4">
                <h6 className="mb-3">{t('account.personalInfo')}</h6>
                <Row className="g-2">
                  <Col sm={6}>
                    <Form.Floating className="mb-2">
                      <Form.Control
                        id="firstName"
                        type="text"
                        placeholder="Voornaam"
                        size="sm"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        required
                        disabled={!permissions.canEditProfile}
                        isInvalid={validationErrors.has('firstName')}
                      />
                      <Form.Label htmlFor="firstName">
                        {t('account.firstName')} <span className="text-danger">*</span>
                      </Form.Label>
                    </Form.Floating>
                  </Col>
                  <Col sm={6}>
                    <Form.Floating className="mb-2">
                      <Form.Control
                        id="lastName"
                        type="text"
                        placeholder="Achternaam"
                        size="sm"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        required
                        disabled={!permissions.canEditProfile}
                        isInvalid={validationErrors.has('lastName')}
                      />
                      <Form.Label htmlFor="lastName">
                        {t('account.lastName')} <span className="text-danger">*</span>
                      </Form.Label>
                    </Form.Floating>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col sm={6}>
                    <Form.Floating className="mb-2">
                      <Form.Control
                        id="email"
                        type="email"
                        placeholder="Email"
                        size="sm"
                        value={formData.email}
                        disabled
                        readOnly
                      />
                      <Form.Label htmlFor="email">
                        {t('account.email')}
                      </Form.Label>
                    </Form.Floating>
                  </Col>
                  <Col sm={6}>
                    <Form.Floating className="mb-2">
                      <Form.Control
                        id="phoneNumber"
                        type="tel"
                        placeholder="Telefoonnummer"
                        size="sm"
                        value={formData.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        disabled={!permissions.canEditProfile}
                      />
                      <Form.Label htmlFor="phoneNumber">
                        {t('account.phoneNumber')}
                      </Form.Label>
                    </Form.Floating>
                  </Col>
                </Row>
              </div>

              {/* Company Information */}
              <div className="mb-4">
                <h6 className="mb-3">{t('account.companyInfo')}</h6>
                <Row className="g-2">
                  <Col sm={6}>
                    <Form.Floating className="mb-2">
                      <Form.Control
                        id="company"
                        type="text"
                        placeholder="Bedrijfsnaam"
                        size="sm"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        required
                        disabled={!permissions.canEditProfile || shouldDisableBillingFields}
                        isInvalid={validationErrors.has('company')}
                      />
                      <Form.Label htmlFor="company">
                        {t('account.company')} <span className="text-danger">*</span>
                      </Form.Label>
                    </Form.Floating>
                  </Col>
                  <Col sm={3}>
                    <Form.Floating className="mb-2">
                      <Form.Control
                        id="vatNumber"
                        type="text"
                        placeholder="BTW nummer"
                        size="sm"
                        value={formData.vatNumber}
                        onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                        onBlur={() => formData.vatNumber.trim() && validateVatNumber()}
                        disabled={!permissions.canEditProfile || shouldDisableBillingFields}
                        required
                        isValid={vatValidation.isValid === true}
                        isInvalid={vatValidation.isValid === false || validationErrors.has('vatNumber')}
                      />
                      <Form.Label htmlFor="vatNumber">
                        {t('account.vatNumber')} <span className="text-danger">*</span>
                      </Form.Label>
                      {vatValidation.isValid === true && (
                        <Form.Control.Feedback type="valid">
                          Geldig BTW nummer
                        </Form.Control.Feedback>
                      )}
                      {vatValidation.isValid === false && (
                        <Form.Control.Feedback type="invalid">
                          {vatValidation.error || 'Ongeldig BTW nummer'}
                        </Form.Control.Feedback>
                      )}
                    </Form.Floating>
                    {vatValidation.isValidating && (
                      <div className="text-center">
                        <small className="text-muted">
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Valideren...
                        </small>
                      </div>
                    )}
                    {vatValidation.validationData && vatValidation.isValid && (
                      <div className="mt-2">
                        <small className="text-success">
                          <strong>{vatValidation.validationData.company_name}</strong><br />
                          {vatValidation.validationData.country_code}: {vatValidation.validationData.vat_number}
                        </small>
                      </div>
                    )}
                  </Col>
                  <Col sm={3}>
                    <Form.Floating className="mb-2">
                      <Form.Control
                        id="kvkNumber"
                        type="text"
                        placeholder="KvK nummer"
                        size="sm"
                        value={formData.kvkNumber}
                        onChange={(e) => handleInputChange('kvkNumber', e.target.value)}
                        disabled={!permissions.canEditProfile || shouldDisableBillingFields}
                        required
                        isInvalid={validationErrors.has('kvkNumber')}
                      />
                      <Form.Label htmlFor="kvkNumber">
                        {t('account.kvkNumber')} <span className="text-danger">*</span>
                      </Form.Label>
                    </Form.Floating>
                  </Col>
                </Row>
              </div>

              {/* Billing Address */}
              {renderAddressForm(
                t('account.billingAddress'),
                formData.billingAddress,
                (field, value) => handleAddressChange('billingAddress', field, value),
                !permissions.canEditProfile || shouldDisableBillingFields,
                'billingAddress',
                (countryCode) => handleCountryChange('billingAddress', countryCode)
              )}

              {/* Customer Service Notice for Disabled Fields */}
              {shouldDisableBillingFields && (
                <Alert variant="info" className="mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  {t('account.contactCustomerService')}
                </Alert>
              )}

              {/* Delivery Address */}
              <Form.Check
                type="checkbox"
                label={t('account.sameAsBilling')}
                checked={sameAsDelivery}
                onChange={(e) => handleSameAsBillingToggle(e.target.checked)}
                disabled={!permissions.canEditProfile}
                className="mb-2"
              />
              
              {renderAddressForm(
                t('account.deliveryAddress'),
                formData.deliveryAddress,
                (field, value) => handleAddressChange('deliveryAddress', field, value),
                sameAsDelivery || !permissions.canEditProfile,
                'deliveryAddress',
                (countryCode) => handleCountryChange('deliveryAddress', countryCode)
              )}

              {/* Unsaved Changes Warning */}
              {hasUnsavedChanges && (
                <div className="alert alert-warning d-flex align-items-center mt-4" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <span>{t('account.unsavedChanges')}</span>
                </div>
              )}

              {/* Action Button */}
              <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                {permissions.showApprovalRequest ? (
                  <Button 
                    variant="success" 
                    onClick={handleRequestApproval}
                    disabled={isSaving}
                    size="sm"
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </span>
                        {t('account.requestingApproval')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        {t('account.requestApproval')}
                      </>
                    )}
                  </Button>
                ) : permissions.canEditProfile ? (
                  <Button 
                    variant="primary" 
                    onClick={handleSave}
                    disabled={isSaving}
                    size="sm"
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </span>
                        {t('account.saving')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {t('account.save')}
                      </>
                    )}
                  </Button>
                ) : null}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserAccountPage;